
import { withTheme } from  'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useCookies } from 'react-cookie';
import { Fragment, useMemo, useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
    FovLoader,
    FovDivider,
    VerticalSpacer,
    FovConfirmation,
    Body,
} from 'components';
import { getPipelineAdmin, flushPipelineAdmin } from 'store/actions/admin';
import {
    MemberContainer,
} from './styles';
import { TabContainer, Tab } from 'pages/ArchVisionAdminDashboard/styles';
import { listColumns, queueListColumns } from './listColumns';

const AdminPipelines = withTheme(props => {
    const { theme } = props;
    const dispatch = useDispatch();

    const userDataStore = useSelector(state => state.userData);
    const adminDataStore = useSelector(state => state.adminData);

    const [ cookies ] = useCookies([ 'userAuth' ]);
    const token = cookies.archvision_token;

    const selectedTheme = userDataStore.userPreferences.theme;
    const pipelines = useMemo(() => adminDataStore.pipelines, [ adminDataStore.pipelines ]);
    const queues = useMemo(() => adminDataStore.queues, [ adminDataStore.queues ]);
    const isLoading = useMemo(() => adminDataStore.isLoading, [ adminDataStore.isLoading ]);
    const pagination = useMemo(() => adminDataStore.pagination, [ adminDataStore.pagination ]);
    const { page, pageSize, totalCount } = pagination;

    const [ searchParams, setSearchParams ] = useSearchParams();
    const [ selectedTab, setSelectedTab ] = useState('Instances');
    const [ showQueueFlush, setShowQueueFlush ] = useState(false);
    const [ isDeleting, setIsDeleting ] = useState(false);
    const [ selectedQueue, setSelectedQueue ] = useState('');


    const tabNames = [ 'Instances', 'Queues' ];

    useEffect(() => {
        if (!pipelines || pipelines.length === 0) {
            dispatch(getPipelineAdmin(token));
        }
    }, []);

        const handlePageChange = page => {
        if (Number(searchParams.get('page')) === page) return;
        searchParams.set('page', page);
        setSearchParams(searchParams);
        dispatch(getPipelineAdmin(token));
    };

    const handleQueueFlush = useCallback(() => {
        const onComplete = () => {
            setShowQueueFlush(false);
            setIsDeleting(false);
            setSelectedQueue('');
        };

        dispatch(flushPipelineAdmin(token, selectedQueue, onComplete));
    }, [ selectedQueue ]);

    const tabIsSelected = name => selectedTab === name;

    const generateTabs = () => {
        return tabNames.map(name => (
            <Tab
                key={name}
                theme={theme}
                $selectedTheme={selectedTheme}
                onClick={() => {
                    setSelectedTab(name);
                }}
                selected={tabIsSelected(name)}
            >
                {name}
            </Tab>
        ));
    };

    return (
        <FovLoader
            text='Retrieving pipeline data'
            resource={!isLoading}
        >
            {pipelines?.length === 0 && queues?.length === 0 && <div>No pipeline or queues.</div>}
            {(pipelines?.length > 0 || queues?.length > 0) &&
                <Fragment>
                    <TabContainer>
                        {generateTabs()}
                    </TabContainer>
                    <FovDivider />
                    <VerticalSpacer size={16} />
                    {tabIsSelected('Instances') && <MemberContainer
                        dataSource={pipelines}
                        $selectedTheme={selectedTheme}
                        columns={listColumns(theme, selectedTheme)}
                        rowKey={'row-' + Math.random()}
                        currentPage={page}
                        pageSize={pageSize}
                        totalCount={totalCount}
                        isPagination={true}
                        selectedKey='id'
                        onPageChange={handlePageChange}
                    />}
                    {tabIsSelected('Queues') && <MemberContainer
                        dataSource={queues}
                        $selectedTheme={selectedTheme}
                        columns={queueListColumns(theme, selectedTheme, setShowQueueFlush, setSelectedQueue)}
                        rowKey={'row-' + Math.random()}
                        currentPage={page}
                        pageSize={pageSize}
                        totalCount={totalCount}
                        isPagination={true}
                        selectedKey='id'
                        onPageChange={handlePageChange}
                    />
                    }
                    <FovConfirmation
                        onConfirm={() => {
                            setIsDeleting(true);
                            handleQueueFlush();
                        }}
                        onClose={() => {
                            setShowQueueFlush(false);
                            setSelectedQueue('');
                        }}
                        confirmationText='Flush queue'
                        isOpened={showQueueFlush}
                        ignoreConfirmClose={true}
                        confirmColor={theme.colors[selectedTheme].error}
                        confirmHoverColor={theme.colors[selectedTheme].errorHover}
                        disabled={isDeleting}
                    >
                        <Body size='Medium'>
                            {
                            `Are you sure you want to flush ${selectedQueue || 'selected queue'}?`
                            }
                        </Body>
                    </FovConfirmation>
                </Fragment>
            }
        </FovLoader>
    );
});

export default AdminPipelines;
